/** @version 0.3.0 */
/**
 * Agent color resolver, ported from Simpliloquy V2 with the DEFAULTS
 * remapped to the BgAgents panel.
 *
 * Resolution order:
 *   1. Override from profile frontmatter or panel.json (palette id, or
 *      explicit hue/chroma) wins.
 *   2. DEFAULTS by slug: a sensible BgAgents-panel-to-palette mapping that
 *      reads warm-to-cool across the spectrum, anchoring jayson at amber.
 *   3. Final fallback: neutral gray.
 *
 * The OKLCH variable production is identical to V2 so any V2 surface can be
 * dropped in without re-tuning the hues.
 */

import type { CSSProperties } from 'react';
import { findPalette } from './palette';

export interface AgentColor {
  hue: number;
  chroma: number;
}

const DEFAULTS: Record<string, AgentColor> = {
  // Warm: alert, outreach, deployment, narration.
  security_compliance:    { hue: 15,  chroma: 1.0  }, // alert
  client_comms:           { hue: 25,  chroma: 0.9  }, // coral
  cloudflare_devops:      { hue: 42,  chroma: 1.0  }, // orange
  jayson:                 { hue: 55,  chroma: 1.0  }, // amber, narrator
  orchestrator:           { hue: 75,  chroma: 0.22 }, // gold, conductor
  hanq:                   { hue: 85,  chroma: 0.45 }, // wheat, gathering
  data_viz_specialist:    { hue: 100, chroma: 0.75 }, // olive
  canonical_guardian:     { hue: 135, chroma: 0.9  }, // forest
  evidence_collector:     { hue: 150, chroma: 1.0  }, // pine
  frontend_developer:     { hue: 175, chroma: 1.0  }, // teal
  accessibility_auditor:  { hue: 195, chroma: 1.0  }, // cyan
  ux_architect:           { hue: 205, chroma: 1.0  }, // sky
  seo_schema_architect:   { hue: 225, chroma: 0.55 }, // slate
  librarian:              { hue: 255, chroma: 1.0  }, // indigo
  credentials_librarian:  { hue: 265, chroma: 1.0  }, // royal
  memory_agent:           { hue: 290, chroma: 0.9  }, // violet
  content_creator:        { hue: 320, chroma: 1.0  }, // magenta
  brand_guardian:         { hue: 345, chroma: 0.95 }, // rose, writer's voice
};

const FALLBACK: AgentColor = { hue: 0, chroma: 0 };

export function getAgentColor(slug: string, override?: { palette?: string; hue?: number; chroma?: number }): AgentColor {
  if (override?.palette) {
    const p = findPalette(override.palette);
    if (p) return { hue: p.hue, chroma: p.chroma };
  }
  if (override && typeof override.hue === 'number' && Number.isFinite(override.hue)) {
    return { hue: override.hue, chroma: typeof override.chroma === 'number' ? override.chroma : 1.0 };
  }
  return DEFAULTS[slug] ?? FALLBACK;
}

export function agentColorVars(slug: string, override?: { palette?: string; hue?: number; chroma?: number }): CSSProperties {
  const { hue, chroma } = getAgentColor(slug, override);
  const c = (base: number): number => +(base * chroma).toFixed(3);
  const vars: Record<string, string | number> = {
    '--agent-h': hue,
    '--agent-c': chroma,
    '--agent-bg':         `oklch(13% ${c(0.025)} ${hue})`,
    '--agent-bg-soft':    `oklch(18% ${c(0.045)} ${hue})`,
    '--agent-bubble':     `oklch(28% ${c(0.06)} ${hue})`,
    '--agent-card-bg':    `oklch(22% ${c(0.075)} ${hue})`,
    '--agent-card-edge':  `oklch(40% ${c(0.10)} ${hue})`,
    '--agent-pill-bg':    `oklch(26% ${c(0.06)} ${hue})`,
    '--agent-pill-edge':  `oklch(38% ${c(0.08)} ${hue})`,
    '--agent-edge':       `oklch(34% ${c(0.06)} ${hue})`,
    '--agent-edge-soft':  `oklch(26% ${c(0.04)} ${hue})`,
    '--agent-accent':     `oklch(72% ${c(0.15)} ${hue})`,
    '--agent-accent-hi':  `oklch(82% ${c(0.17)} ${hue})`,
    '--agent-accent-lo':  `oklch(55% ${c(0.11)} ${hue})`,
    '--agent-subtle':     `oklch(62% ${c(0.09)} ${hue})`,
    '--agent-fg-body':    `oklch(92% ${c(0.012)} ${hue})`,
    '--agent-fg-soft':    `oklch(78% ${c(0.015)} ${hue})`,
    '--agent-user':       `oklch(76% ${c(0.10)} ${(hue + 180) % 360})`,
    '--agent-user-soft':  `oklch(24% ${c(0.040)} ${(hue + 180) % 360})`,
    '--agent-user-edge':  `oklch(36% ${c(0.060)} ${(hue + 180) % 360})`,
    '--agent-user-fg':    `oklch(94% ${c(0.012)} ${(hue + 180) % 360})`,
    // Warm accents (hue 35–110, the green-yellow band) need a near-black
    // foreground for legibility; everything else takes a paper-white. Both
    // values are OKLCH so the brand stays in one color space (no hex literals
    // sneaking back in).
    '--agent-fg-on-accent':
      hue > 35 && hue < 110 ? `oklch(14% 0.005 ${hue})` : `oklch(98% 0.003 ${hue})`,
  };
  return vars as CSSProperties;
}

export function agentDotStyle(slug: string, override?: { palette?: string; hue?: number; chroma?: number }): CSSProperties {
  const { hue, chroma } = getAgentColor(slug, override);
  return {
    backgroundColor: `oklch(68% ${(0.16 * chroma).toFixed(3)} ${hue})`,
    boxShadow: `0 0 6px oklch(64% ${(0.18 * chroma).toFixed(3)} ${hue} / 0.5)`,
  };
}
